<script lang="ts">
	import { updateItemInCart } from '$lib/js/magento/cart/cart'
	import { storeCartItems, storeConfigurableChild, storeRemoveModal, storeStockModal } from '$lib/js/stores'
	import type { CartProduct } from '$lib/js/types'

	interface Props {
		qty?: number;
		prod: CartProduct;
		type?: string; // nullable (to not show the delete modal when qty = 0)
	}

	let { qty = $bindable(1), prod = $bindable(), type = '' }: Props = $props();

	if (prod && prod.cart_qty) {
		qty = prod.cart_qty
	}

	/* Reduce product quantity */
	function reduceQty() {
		if (qty > 1) {
			qty -= 1
			updateQty()
		} else if (type !== 'nullable') {
			$storeRemoveModal = prod.item_id
		}
	}

	function increaseQty() {
		if (
			(prod.stock.source_quantity > qty && prod.type_id !== 'configurable') ||
			(prod.type_id === 'configurable' && $storeConfigurableChild?.stock?.source_quantity > qty)
		) {
			qty += 1
		} else {
			$storeStockModal = prod.stock.source_quantity
		}

		updateQty()
	}

	function updateQty() {
		if (type !== 'nullable' && 
			(prod.stock.source_quantity >= qty && prod.type_id !== 'configurable') ||
			(prod.type_id === 'configurable' && $storeConfigurableChild?.stock?.source_quantity >= qty)) {
			prod.cart_qty = qty
			updateItemInCart(prod)
			$storeCartItems = $storeCartItems.map(item => 
				item.item_id === prod.item_id ? { ...item, cart_qty: qty } : item
			)
		} else if (type !== 'nullable' && prod.stock.source_quantity < qty) {
			$storeStockModal = prod.stock.source_quantity
		}
	}

	function changeQty(e: FocusEvent) {
		let new_val = parseInt((<HTMLInputElement>e.target).value, 10)

		if (new_val && type !== 'nullable' && prod.stock.source_quantity >= new_val) {
			qty = new_val
			prod.cart_qty = qty
			updateItemInCart(prod)
		} else if (new_val && type !== 'nullable' && prod.stock.source_quantity < new_val) {
			$storeStockModal = prod.stock.source_quantity

			/* Set max qty available */
			qty = prod.stock.source_quantity
			prod.cart_qty = qty
			updateItemInCart(prod)
		} else if (new_val && type === 'nullable') {
			qty = new_val
		}
	}
</script>

<div class="qty-wrapper {type}">
	<button aria-label="-" class="icon icon-minus-full" onclick={() => reduceQty()}></button>
	<input type="number" min="1" value={qty} onblur={changeQty} />
	<button aria-label="+" class="icon icon-plus-full" onclick={() => increaseQty()}></button>
</div>

<style lang="scss">
	.qty-wrapper {
		display: flex;
		align-items: center;
		background: white;

		.icon-minus-full,
		.icon-plus-full {
			position: relative;
			width: 32px;
			height: 30px;
			display: flex;
			justify-content: center;
			align-items: center;
			transition: all linear 0.125s;
			cursor: pointer;
			border: 1px solid var(--grey--b);

			&::before {
				content: '';
				position: absolute;
				border-bottom: 1px solid black;
				width: 14px;
			}
		}

		.icon-plus-full {
			&::after {
				content: '';
				position: absolute;
				border-bottom: 1px solid black;
				width: 14px;
				transform: rotate(90deg);
			}
		}

		input {
			height: 20px;
			width: 24px;
			background: transparent;
			text-align: center;
			padding: 4px 5px;
			color: var(--grey--b);
			border: 1px solid var(--grey--b);
			transition: all linear 0.125s;
			-webkit-appearance: none;
			-moz-appearance: textfield;
			margin: 0 -1px;

			&:hover,
			&:focus {
				border: 1px solid #cad7d5;
				outline: 0;
			}

			&::-webkit-outer-spin-button,
			&::-webkit-inner-spin-button {
				-webkit-appearance: none;
				margin: 0;
			}
		}

		&.nullable {
			height: 40px;

			.icon-minus-full,
			.icon-plus-full {
				border: none;
				width: 38px;
				height: 40px;
			}

			input {
				border: none;
				color: black;
				border-left: 1px solid var(--grey--border);
				border-right: 1px solid var(--grey--border);
			}
		}
	}
</style>
